import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Collapse, Row, Col, Modal, ModalBody, ModalFooter, Button } from 'reactstrap';

import PhotoGallery from '../SharedComponents/PhotoGallery';
import FileViewer from '../SharedComponents/FileViewer';
import FileUpload from '../SharedComponents/FileUpload';
import MessageLogsContainer from '../Dispatch/HelperComponents/MessageLogsContainer';
import { formatTimestamp } from '../../helpers/formatter';
import dashboardConfig from '../../config';
import { getStoredCustomerDriverReview } from '../../utils/customerDriverReviewStorage';
import * as payment_urls from '../../urls/api-payments/payment';
import * as user_urls from '../../urls/api-users/admin';
import * as shipment_urls from '../../urls/api-shipments/merchant';
import { adminCustomerDriverReviewByShipment, getResolveExternalDriverPaymentUrl } from '../../urls/api-shipments/admin';
import { isLocalLikeHost } from '../../utils/environmentMode';

const CLAIMED_CHAT_ROUTE_STATUSES = new Set(['route_claimed', 'route_assigned', 'route_enroute', 'route_completed', 'active']);
const INVOICE_STATUS_POLL_INTERVAL_MS = 15000;
const MAPBOX_STATIC_TOKEN = 'pk.eyJ1IjoiZ29waHItZW5naW5lZXJpbmciLCJhIjoiY2syYzd6NjdrMmo3azNtcGU1cXZzMTh1MSJ9.cHMxwcG_MoQk-l4ZO3bdEA';

const CHAT_BUBBLE_STYLE = {
  alignItems: 'center',
  border: '1px solid rgba(247, 148, 29, 0.55)',
  borderRadius: '999px',
  color: '#f7941d',
  display: 'inline-flex',
  fontSize: '14px',
  fontWeight: 700,
  height: '24px',
  justifyContent: 'center',
  lineHeight: 1,
  minWidth: '24px',
  padding: '0 6px'
};

const normalizeCustomerDriverReview = review => {
  if (!review || typeof review !== 'object') {
    return null;
  }

  const rating = Number(review.rating || 0);
  if (!Number.isFinite(rating) || rating < 1) {
    return null;
  }

  return {
    rating,
    reviewText: typeof review.review_text === 'string'
      ? review.review_text.trim()
      : (typeof review.reviewText === 'string' ? review.reviewText.trim() : ''),
    submittedAt: review.submitted_at || review.submittedAt || null,
  };
};

const formatEmploymentTypeLabel = employmentType => {
  const normalized = String(employmentType || '')
    .replace(/1099/gi, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([/,&-])/g, '$1')
    .replace(/([/,&-])\s+/g, '$1 ')
    .trim();

  return normalized || null;
};

const isStripeInvoiceId = value => {
  if (typeof value !== 'string') {
    return false;
  }

  return /^in_[A-Za-z0-9]+$/.test(value.trim());
};

const resolveStripeInvoiceId = billingDetails => {
  if (!billingDetails || typeof billingDetails !== 'object') {
    return null;
  }

  const directCandidates = [
    billingDetails.invoice_id,
    billingDetails.gateway_id,
    billingDetails.stripe_invoice_gateway_id,
    billingDetails.id,
  ];

  for (const candidate of directCandidates) {
    if (isStripeInvoiceId(candidate)) {
      return String(candidate).trim();
    }
  }

  return null;
};

const deriveInvoicePaymentSnapshot = shipment => {
  const billingDetails = shipment && typeof shipment.billing_details === 'object' ? shipment.billing_details : null;
  const deferredBilling = billingDetails && typeof billingDetails.deferred_billing === 'object'
    ? billingDetails.deferred_billing
    : null;

  const normalizedBillingStatus = String(shipment && shipment.billing_status ? shipment.billing_status : '')
    .trim()
    .toLowerCase();
  const normalizedInvoiceStatus = String(billingDetails && (billingDetails.invoice_status || billingDetails.status) ? (billingDetails.invoice_status || billingDetails.status) : '')
    .trim()
    .toLowerCase();
  const hasExplicitPaidFlag = billingDetails && Object.prototype.hasOwnProperty.call(billingDetails, 'paid');
  const explicitPaidFlag = hasExplicitPaidFlag ? billingDetails.paid === true : null;
  const normalizedDeferredStatus = String(deferredBilling && deferredBilling.status ? deferredBilling.status : '')
    .trim()
    .toLowerCase();
  const normalizedCode = String(billingDetails && billingDetails.code ? billingDetails.code : '')
    .trim()
    .toUpperCase();

  if (explicitPaidFlag === true || normalizedInvoiceStatus === 'paid') {
    return { paid: true, status: 'paid' };
  }

  if (explicitPaidFlag === false) {
    return { paid: false, status: normalizedInvoiceStatus || 'open' };
  }

  if (normalizedDeferredStatus === 'completed' || normalizedBillingStatus === 'completed') {
    return { paid: true, status: 'paid' };
  }

  if (normalizedDeferredStatus === 'pending_assignment_capture' || normalizedDeferredStatus === 'pending_checkout') {
    return { paid: false, status: normalizedDeferredStatus };
  }

  if (normalizedDeferredStatus === 'failed' || normalizedCode === 'INVOICE_ERROR' || normalizedCode === 'CARD_DECLINED') {
    return { paid: false, status: 'failed' };
  }

  if (billingDetails && (billingDetails.invoice_id || billingDetails.hosted_invoice_url || billingDetails.invoice_pdf)) {
    return { paid: false, status: 'open' };
  }

  if (normalizedBillingStatus === 'billable') {
    return { paid: false, status: 'pending' };
  }

  return { paid: null, status: null };
};

const formatInvoiceStatusLabel = ({ loading, paid, status }) => {
  if (loading) {
    return '...';
  }

  if (paid === true) {
    return 'Paid';
  }

  const normalizedStatus = String(status || '').trim().toLowerCase();
  if (!normalizedStatus) {
    return 'Unknown';
  }

  if (normalizedStatus === 'pending_assignment_capture' || normalizedStatus === 'pending_checkout' || normalizedStatus === 'pending') {
    return 'Pending';
  }

  if (normalizedStatus === 'failed' || normalizedStatus === 'uncollectible') {
    return 'Failed';
  }

  if (normalizedStatus === 'open' || normalizedStatus === 'draft') {
    return 'Open';
  }

  if (normalizedStatus === 'void') {
    return 'Void';
  }

  return normalizedStatus;
};

const COMPLETED_MARK = (
  <span role="img" aria-label="completed">
    {' '}check mark
  </span>
);

const Detailed = props => {
  const disableInvoiceStatusFetch = props && props.disableInvoiceStatusFetch === true;
  const [isOpen, setIsOpen] = useState(false);
  const [invoicePaymentStatus, setInvoicePaymentStatus] = useState(() => ({
    loading: false,
    ...deriveInvoicePaymentSnapshot(props.shipment || {}),
  }));
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatContact, setChatContact] = useState({});
  const [shipmentChatOpen, setShipmentChatOpen] = useState(false);
  const [shipmentChatLoading, setShipmentChatLoading] = useState(false);
  const [shipmentChatSending, setShipmentChatSending] = useState(false);
  const [shipmentChatMessages, setShipmentChatMessages] = useState([]);
  const [shipmentChatMessage, setShipmentChatMessage] = useState('');
  const [shipmentChatInfo, setShipmentChatInfo] = useState(null);
  const [mapSnapshotProvider, setMapSnapshotProvider] = useState('google');
  const [isMapSnapshotOpen, setIsMapSnapshotOpen] = useState(false);
  const shipment = props.shipment || {};
  const profiledUser = props.user || {};
  const customer = shipment.customer_blob || {};
  const orders = Array.isArray(shipment.Orders) ? shipment.Orders : [];
  const route = shipment.Route || {};
  const routeDrivers = Array.isArray(route.RouteDrivers) ? route.RouteDrivers : [];
  const shipmentHandshakes = Array.isArray(shipment.ShipmentHandshakes) ? shipment.ShipmentHandshakes : [];
  const activeDriverPays = Array.isArray(shipment.DriverPays)
    ? shipment.DriverPays.filter(pay => pay && !pay.deleted_at)
    : [];
  const completedAtDisplayValue = shipment.completed_at || shipment.updated_at;
  const signatureHandshake = shipmentHandshakes.length > 0 ? shipmentHandshakes[0] : null;
  const deadDropPhotos = signatureHandshake && signatureHandshake.type === 'dead_drop' ? [signatureHandshake.url] : [];
  const expandedRowColumns = 6;
  const isAdminViewer = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin/');
  const isShipmentArchived = shipment.status === 'shipment_completed' || Boolean(shipment.completed_at);
  const claimedOrderChatEndpoint = `${shipment_urls.shipmentHost}/dispatch/chat/thread`;
  const billingDetails = shipment && typeof shipment.billing_details === 'object' ? shipment.billing_details : null;
  const invoiceId = resolveStripeInvoiceId(billingDetails);
  const shipmentId = shipment && shipment.shipment_id ? String(shipment.shipment_id) : '';
  const shipmentFriendlyId = shipment && shipment.shipment_friendly_id ? String(shipment.shipment_friendly_id) : '';
  const storedCustomerDriverReview = shipmentId ? normalizeCustomerDriverReview(getStoredCustomerDriverReview(shipmentId)) : null;
  const [customerDriverReview, setCustomerDriverReview] = useState(() => storedCustomerDriverReview);
  const [driverPayResolution, setDriverPayResolution] = useState({
    open: false,
    loading: false,
    payRecords: [],
    reference: '',
    note: '',
    resolvingPayId: '',
    message: '',
    error: '',
    resolvedPayIds: [],
  });

  useEffect(() => {
    const googleMapAccessKey = dashboardConfig && dashboardConfig.config && dashboardConfig.config.GOOGLE_MAP
      ? dashboardConfig.config.GOOGLE_MAP.accessKey
      : '';

    setMapSnapshotProvider(googleMapAccessKey ? 'google' : 'mapbox');
  }, [shipmentId, shipmentFriendlyId]);

  useEffect(() => {
    // Clear immediately when shipment changes to prevent stale review from prior shipment showing
    setCustomerDriverReview(storedCustomerDriverReview || null);
  }, [shipmentId]);

  useEffect(() => {
    if (!isOpen || !shipmentId) {
      return undefined;
    }

    let isActive = true;

    // Clear stale data before fetching so the prior shipment's review doesn't show
    setCustomerDriverReview(null);

    axios({
      method: 'get',
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        'content-type': 'application/json'
      },
      url: adminCustomerDriverReviewByShipment(shipmentId)
    }).then(response => {
      if (!isActive) {
        return;
      }

      const payload = response && response.data ? response.data.payload : null;
      const remoteReview = normalizeCustomerDriverReview(payload);
      setCustomerDriverReview(remoteReview || storedCustomerDriverReview || null);
    }).catch(() => {
      if (!isActive) {
        return;
      }

      setCustomerDriverReview(storedCustomerDriverReview || null);
    });

    return () => {
      isActive = false;
    };
  }, [isOpen, shipmentId, storedCustomerDriverReview]);

  useEffect(() => {
    if (disableInvoiceStatusFetch) {
      const fallbackSnapshot = deriveInvoicePaymentSnapshot(shipment);
      setInvoicePaymentStatus({ loading: false, ...fallbackSnapshot });
      return undefined;
    }

    let isActive = true;
    let pollTimer = null;
    const fallbackSnapshot = deriveInvoicePaymentSnapshot(shipment);

    setInvoicePaymentStatus(current => ({
      loading: current.loading,
      paid: current.paid === true ? true : fallbackSnapshot.paid,
      status: current.paid === true ? 'paid' : fallbackSnapshot.status,
    }));

    const fetchInvoiceStatus = ({ showLoading = false } = {}) => {
      if (!invoiceId) {
        if (isActive) {
          setInvoicePaymentStatus({
            loading: false,
            ...fallbackSnapshot,
          });
        }

        return Promise.resolve();
      }

      if (showLoading) {
        setInvoicePaymentStatus(current => ({ ...current, loading: true }));
      }

      const headers = {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        'content-type': 'application/json'
      };
      const statusRequest = axios({
        method: 'get',
        headers,
        url: `${payment_urls.paymentHost}/v2/invoices/status/${encodeURIComponent(invoiceId)}`
      });

      return statusRequest
        .then(response => {
          if (!isActive) {
            return;
          }

          const payload = response && response.data && response.data.payload ? response.data.payload : null;

          setInvoicePaymentStatus({
            loading: false,
            paid: Boolean(payload && payload.paid === true),
            status: payload && payload.status ? payload.status : fallbackSnapshot.status,
          });
        })
        .catch(() => {
          if (!isActive) {
            return;
          }

          setInvoicePaymentStatus({
            loading: false,
            ...fallbackSnapshot,
          });
        });
    };

    if (!invoiceId && !shipmentId && !shipmentFriendlyId) {
      setInvoicePaymentStatus({ loading: false, ...fallbackSnapshot });
      return () => {
        isActive = false;
      };
    }

    fetchInvoiceStatus({ showLoading: invoicePaymentStatus.paid === null });

    if (invoiceId && invoicePaymentStatus.paid !== true) {
      pollTimer = window.setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          return;
        }

        fetchInvoiceStatus();
      }, INVOICE_STATUS_POLL_INTERVAL_MS);
    }

    return () => {
      isActive = false;

      if (pollTimer !== null) {
        window.clearInterval(pollTimer);
      }
    };
  }, [disableInvoiceStatusFetch, invoiceId, invoicePaymentStatus.paid, shipment, shipmentId, shipmentFriendlyId]);

  const getShipmentActionHost = currentShipment => {
    if (currentShipment && currentShipment.__actionHost) {
      return currentShipment.__actionHost;
    }

    if (isLocalLikeHost(shipment_urls.shipmentHost) && !isLocalLikeHost(shipment_urls.hasuraHost)) {
      return shipment_urls.hostedShipmentHost;
    }

    return shipment_urls.shipmentHost;
  };

  const toggle = () => setIsOpen(!isOpen);

  const toggleChatModal = () => setShowChatModal(current => !current);

  const resetShipmentChatState = () => {
    setShipmentChatOpen(false);
    setShipmentChatLoading(false);
    setShipmentChatSending(false);
    setShipmentChatMessages([]);
    setShipmentChatMessage('');
    setShipmentChatInfo(null);
  };

  const formatCurrency = value => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(2) : '0.00';
  };

  const formatStatus = status => {
    switch (status) {
      case 'shipment_saved':
        return 'Saved';
      case 'shipment_created':
        return 'Created';
      case 'shipment_scheduled':
        return 'Scheduled';
      case 'shipment_accepted':
        return 'Accepted';
      case 'shipment_ready_for_driver':
        return 'Ready for Driver';
      case 'shipment_claimed':
        return 'Claimed';
      case 'shipment_assigned':
        return 'Assigned';
      case 'shipment_enroute':
        return 'En Route';
      case 'shipment_completed':
        return 'Completed';
      case 'shipment_declined':
        return 'Declined';
      case 'shipment_cancelled':
        return 'Cancelled';
      default:
        return status || 'Unknown';
    }
  };

  const getShipmentChatRouteId = currentShipment => currentShipment.route_id || (currentShipment.Route && currentShipment.Route.route_id) || '';

  const getShipmentChatRouteStatus = currentShipment => (currentShipment.Route && currentShipment.Route.status) || '';

  const isLikelyUserIdentifier = value => {
    if (!value || typeof value !== 'string') {
      return false;
    }

    return value.includes('|') || /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(value);
  };

  const normalizeAuth0Candidate = value => {
    if (!value || typeof value !== 'string') {
      return '';
    }

    const trimmedValue = value.trim();
    const strippedValue = trimmedValue.replace(/\(behalf\)$/i, '').trim();

    return isLikelyUserIdentifier(strippedValue) ? strippedValue : '';
  };

  const getShipmentChatParticipantAuth0Id = currentShipment => {
    const customerBlob = currentShipment && currentShipment.customer_blob && typeof currentShipment.customer_blob === 'object'
      ? currentShipment.customer_blob
      : {};

    return normalizeAuth0Candidate(currentShipment.cust_auth0_id)
      || normalizeAuth0Candidate(currentShipment.created_by)
      || normalizeAuth0Candidate(currentShipment.placed_by)
      || normalizeAuth0Candidate(customerBlob.auth0_id)
      || normalizeAuth0Candidate(profiledUser.auth0_id)
      || '';
  };

  const getShipmentChatCustomerDisplayName = currentShipment => {
    const customerBlob = currentShipment && currentShipment.customer_blob && typeof currentShipment.customer_blob === 'object'
      ? currentShipment.customer_blob
      : {};
    const fullName = `${customerBlob.first_name || ''} ${customerBlob.last_name || ''}`.trim();
    return fullName || getOrderPlacedByName() || 'Customer';
  };

  const getShipmentChatDriverDisplayName = currentShipment => {
    const currentDriver = getCurrentRouteDriver();
    const driverBlob = currentDriver && currentDriver.driver_blob ? currentDriver.driver_blob : {};
    const fullName = `${driverBlob.first_name || ''} ${driverBlob.last_name || ''}`.trim();
    return fullName || 'Driver';
  };

  const isCustomerDispatchChatAvailable = currentShipment => Boolean(getShipmentChatRouteId(currentShipment)) && Boolean(getShipmentChatParticipantAuth0Id(currentShipment));

  const isCustomerDriverChatAvailable = currentShipment => Boolean(getShipmentChatRouteId(currentShipment)) &&
    CLAIMED_CHAT_ROUTE_STATUSES.has(getShipmentChatRouteStatus(currentShipment)) &&
    Boolean(getCurrentRouteDriver()) &&
    Boolean(getShipmentChatParticipantAuth0Id(currentShipment));

  const buildShipmentChatInfo = threadType => {
    const participantAuth0Id = getShipmentChatParticipantAuth0Id(shipment);
    const customerDisplayName = getShipmentChatCustomerDisplayName(shipment);
    const driverDisplayName = getShipmentChatDriverDisplayName(shipment);

    return {
      chatMode: 'order-thread',
      chatShipmentId: shipment.shipment_id,
      chatShipmentFriendlyId: shipment.shipment_friendly_id || '',
      chatThreadType: threadType,
      chatParticipantAuth0Id: participantAuth0Id,
      chatDisplayName: threadType === 'customer_dispatch' ? customerDisplayName : `${customerDisplayName} / ${driverDisplayName}`,
      chatRole: threadType === 'customer_dispatch' ? 'Customer Dispatch' : 'Customer Driver',
      chatCustomerDisplayName: customerDisplayName,
      chatDriverDisplayName: threadType === 'customer_dispatch' ? 'Dispatch' : driverDisplayName,
      chatAdminDisplayName: customerDisplayName,
      chatTitle: threadType === 'customer_dispatch' ? 'Customer / Dispatch Chat' : 'Customer / Driver Chat'
    };
  };

  const fetchShipmentChatThread = nextChatInfo => {
    if (!nextChatInfo || !nextChatInfo.chatShipmentId || !nextChatInfo.chatThreadType) {
      setShipmentChatMessages([]);
      return Promise.resolve();
    }

    setShipmentChatLoading(true);

    return axios({
      method: 'get',
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        'content-type': 'application/json'
      },
      url: `${claimedOrderChatEndpoint}?shipment_id=${encodeURIComponent(nextChatInfo.chatShipmentId)}&thread_type=${encodeURIComponent(nextChatInfo.chatThreadType)}&participant_auth0_id=${encodeURIComponent(nextChatInfo.chatParticipantAuth0Id || '')}`
    })
      .then(response => {
        const payload = response && response.data ? response.data.payload || {} : {};
        setShipmentChatMessages(Array.isArray(payload.messages) ? payload.messages : []);
      })
      .catch(() => {
        setShipmentChatMessages([]);
      })
      .finally(() => {
        setShipmentChatLoading(false);
      });
  };

  const openShipmentChat = threadType => {
    const isAvailable = threadType === 'customer_dispatch'
      ? isCustomerDispatchChatAvailable(shipment)
      : isCustomerDriverChatAvailable(shipment);

    if (!isAvailable) {
      return;
    }

    const nextChatInfo = buildShipmentChatInfo(threadType);

    if (!nextChatInfo.chatParticipantAuth0Id) {
      return;
    }

    setShipmentChatInfo({
      ...nextChatInfo,
      chatArchived: isShipmentArchived,
    });
    setShipmentChatMessage('');
    setShipmentChatOpen(true);
    void fetchShipmentChatThread(nextChatInfo);
  };

  const sendShipmentChatMessage = () => {
    if (isShipmentArchived) {
      return;
    }

    const trimmedMessage = shipmentChatMessage.trim();

    if (!shipmentChatInfo || !shipmentChatInfo.chatShipmentId || !shipmentChatInfo.chatThreadType || !trimmedMessage) {
      return;
    }

    setShipmentChatSending(true);

    axios({
      method: 'post',
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        'content-type': 'application/json'
      },
      url: claimedOrderChatEndpoint,
      data: {
        shipment_id: shipmentChatInfo.chatShipmentId,
        thread_type: shipmentChatInfo.chatThreadType,
        participant_auth0_id: shipmentChatInfo.chatParticipantAuth0Id,
        body: trimmedMessage,
      }
    })
      .then(response => {
        const createdMessage = response && response.data && response.data.payload ? response.data.payload : null;

        if (!createdMessage) {
          return;
        }

        setShipmentChatMessages(currentMessages => [...currentMessages, createdMessage]);
        setShipmentChatMessage('');
      })
      .finally(() => {
        setShipmentChatSending(false);
      });
  };

  const getVehicleTypeLabel = vehicleType => {
    switch (vehicleType) {
      case 'truck':
        return '1/2 Ton Pickup';
      case 'heavy_duty_truck':
        return '1 Ton Pickup';
      case 'sedan':
        return 'Sedan';
      case 'small_suv':
        return 'Small SUV';
      case 'large_suv':
        return 'Large SUV';
      case 'pickup_truck':
        return '1/2 Ton Pickup';
      case 'cargo_van':
        return 'Cargo Van';
      case 'box_truck':
        return 'Box Truck';
      default:
        return vehicleType || 'Unknown';
    }
  };

  const getShipmentTypeLabel = shipmentType => {
    switch (shipmentType) {
      case 'direct':
      case 'routed_direct':
      case 'multidrop_direct':
        return 'Direct';
      case 'bridge':
        return 'Bridge';
      case 'marketplace':
        return 'Marketplace';
      case 'school_lunch':
        return 'School Lunch';
      default:
        return shipmentType || 'Unknown';
    }
  };

  const getOrderPlacedByName = () => {
    return [customer.first_name, customer.last_name].filter(Boolean).join(' ') || shipment.placed_by || customer.email || customer.store_name || 'Unknown Customer';
  };

  const normalizePhoneForChat = phoneNumber => {
    if (!phoneNumber) {
      return '';
    }

    const rawPhone = String(phoneNumber).trim();
    if (rawPhone === '') {
      return '';
    }

    if (rawPhone.startsWith('+')) {
      const digitsWithPlus = '+' + rawPhone.slice(1).replace(/\D/g, '');
      return digitsWithPlus.length > 1 ? digitsWithPlus : '';
    }

    const digits = rawPhone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `+1${digits}`;
    }

    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }

    return '';
  };

  const getChatInitials = contactInfo => {
    const displayName = (contactInfo && (contactInfo.chatDisplayName || contactInfo.displayName)) || '';
    return displayName
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(namePart => namePart.charAt(0).toUpperCase())
      .join('') || 'C';
  };

  const getChatTitle = contactInfo => {
    const role = contactInfo && contactInfo.chatRole ? contactInfo.chatRole : '';

    if (role === 'Order Placer') {
      return 'Order Placed By Chat';
    }

    if (role === 'Pickup') {
      return 'Pickup Location Chat';
    }

    if (role === 'Dropoff') {
      return 'Dropoff Location Chat';
    }

    if (role === 'Driver') {
      return 'Driver Chat';
    }

    return 'Chat';
  };

  const openChatHistory = contactInfo => {
    const normalizedPhone = normalizePhoneForChat(contactInfo && contactInfo.phone);

    if (!isAdminViewer || !normalizedPhone) {
      return;
    }

    const nextContact = {
      ...contactInfo,
      phone: normalizedPhone,
      chatShipmentFriendlyId: shipment && shipment.shipment_friendly_id ? shipment.shipment_friendly_id : '',
      chatArchived: isShipmentArchived,
    };

    setChatContact(nextContact);
    setChatMessages([]);
    setShowChatModal(true);
    setChatLoading(true);

    const authHeaders = {
      Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      'content-type': 'application/json'
    };
    const shipmentId = shipment && shipment.shipment_id;
    // Scope the dispatch↔customer (Order Placer) chat to only this order's window, not the whole phone history.
    const scopeToOrder = contactInfo && contactInfo.chatRole === 'Order Placer' && shipmentId;
    const windowRequest = scopeToOrder
      ? axios({ method: 'get', headers: authHeaders, url: shipment_urls.shipmentHost + `/dispatch/chat/order-placer?shipment_id=${encodeURIComponent(shipmentId)}` })
          .then(response => (response && response.data && response.data.payload) || null)
          .catch(() => null)
      : Promise.resolve(null);

    windowRequest.then(orderWindow => {
      axios({
        method: 'get',
        headers: authHeaders,
        url: user_urls.dispatchUsersHost + `/driver/fetch-messages?phoneNumber=${encodeURIComponent(normalizedPhone)}`
      })
        .then(response => {
          let messages = response.data.status === 'successful' && Array.isArray(response.data.payload) ? response.data.payload : [];
          if (orderWindow && (orderWindow.window_start || orderWindow.window_end)) {
            const startMs = orderWindow.window_start ? new Date(orderWindow.window_start).getTime() : -Infinity;
            const endMs = orderWindow.window_end ? new Date(orderWindow.window_end).getTime() : Infinity;
            messages = messages.filter(message => {
              const stamp = new Date(message && (message.time || message.created_at || message.dateCreated)).getTime();
              if (!Number.isFinite(stamp)) {
                return true;
              }
              return stamp > startMs && stamp < endMs;
            });
          }
          setChatMessages(messages);
        })
        .catch(() => {
          setChatMessages([]);
        })
        .finally(() => {
          setChatLoading(false);
        });
    });
  };

  const renderPhoneWithBubble = phone => {
    if (!phone) {
      return null;
    }

    return (
      <p style={{ alignItems: 'center', display: 'flex', gap: '8px', marginBottom: 0 }}>
        <span>{phone}</span>
        {isAdminViewer ? (
            <button
            type="button"
            onClick={() => openChatHistory({
              chatDisplayName: getOrderPlacedByName(),
              chatRole: 'Order Placer',
              displayName: getOrderPlacedByName(),
              phone,
            })}
            aria-label="Open chat history"
            title="Open chat history"
            style={{ background: 'transparent', border: 'none', padding: 0 }}
              disabled={isShipmentArchived}
          >
            <span aria-label="Chat bubble" style={CHAT_BUBBLE_STYLE}>...</span>
          </button>
        ) : null}
      </p>
    );
  };

  const getCurrentRouteDriver = () => {
    if (routeDrivers.length === 0) {
      return null;
    }

    return routeDrivers.find(driver => driver && driver.status !== 'unassigned') || routeDrivers[0];
  };

  const getDriverPayAmount = driver => {
    if (!driver) {
      return null;
    }

    const matchingPays = activeDriverPays.filter(pay => {
      if (!pay) {
        return false;
      }

      if (pay.driver_id && driver.driver_id) {
        return pay.driver_id === driver.driver_id && (pay.driver_status === 'driver' || !pay.driver_status);
      }

      return pay.driver_status === 'driver' || !pay.driver_status;
    });

    if (matchingPays.length > 0) {
      return matchingPays.reduce((sum, pay) => sum + Number(pay.total_earning || 0), 0);
    }

    if (Number.isFinite(Number(shipment.driver_1099_earning))) {
      return Number(shipment.driver_1099_earning);
    }

    return null;
  };

  const getShipmentFeeBlob = () => {
    return shipment.shipping_fee_blob && typeof shipment.shipping_fee_blob === 'object' ? shipment.shipping_fee_blob : {};
  };

  const loadUnpaidDriverPays = () => {
    if (!shipmentId) {
      return;
    }

    setDriverPayResolution(prev => ({ ...prev, open: true, loading: true, error: '', message: '' }));
    axios({
      method: 'post',
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        'content-type': 'application/json',
      },
      url: shipment_urls.hasuraHost,
      data: {
        query: `{ DriverPays(where: { shipment_id: {_eq: "${shipmentId}"}, deleted_at: {_is_null: true} }) { pay_id payed_status total_earning driver_id driver_status first_name last_name } }`,
      },
    })
      .then(response => {
        const records = (response.data && response.data.data && response.data.data.DriverPays) || [];
        setDriverPayResolution(prev => ({ ...prev, loading: false, payRecords: records }));
      })
      .catch(() => {
        setDriverPayResolution(prev => ({ ...prev, loading: false, error: 'Failed to load driver pay records.' }));
      });
  };

  const resolveDriverPayExternally = payRecord => {
    const reference = driverPayResolution.reference.trim();
    const note = driverPayResolution.note.trim();
    if (!reference || !note) {
      setDriverPayResolution(prev => ({ ...prev, error: 'Payment reference and resolution note are required.' }));
      return;
    }

    setDriverPayResolution(prev => ({ ...prev, resolvingPayId: payRecord.pay_id, error: '', message: '' }));
    axios({
      method: 'put',
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        'content-type': 'application/json',
      },
      url: shipment_urls.toHostedShipmentUrl(getResolveExternalDriverPaymentUrl(shipmentId, payRecord.pay_id)),
      data: { resolution_reference: reference, note },
    })
      .then(() => {
        setDriverPayResolution(prev => ({
          ...prev,
          resolvingPayId: '',
          reference: '',
          note: '',
          resolvedPayIds: [...prev.resolvedPayIds, payRecord.pay_id],
          message: 'Driver payment marked resolved outside Branch.',
        }));
      })
      .catch(error => {
        const errorMessage = (error.response && error.response.data && (error.response.data.message || error.response.data.error))
          || 'Unable to resolve driver payment.';
        setDriverPayResolution(prev => ({ ...prev, resolvingPayId: '', error: errorMessage }));
      });
  };

  const getPickupOrder = () => orders.find(order => order && order.actions && Object.prototype.hasOwnProperty.call(order.actions, 'pick_up')) || orders[0] || null;

  const getDropoffOrder = () => orders.find(order => order && order.actions && Object.prototype.hasOwnProperty.call(order.actions, 'drop_off')) || orders[orders.length - 1] || null;

  const renderOrderHeader = () => {
    const orderPlacedBy = getOrderPlacedByName();

    return (
      <Row style={{ paddingLeft: '20px', paddingRight: '20px', paddingTop: '10px', paddingBottom: '10px' }}>
        <Col className="col-12" style={{ color: '#cdcaca', textAlign: 'left' }}>
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap' }}>
            <strong>Type: {getShipmentTypeLabel(shipment.shipment_type)} &nbsp;&nbsp; -- &nbsp;&nbsp; Ship_ID : {shipment.shipment_friendly_id || 'N/A'}</strong>
            {shipment.is_expedited ? <span style={{ color: '#f7941d', fontWeight: 600, marginLeft: '6px' }}>Expedited Delivery</span> : null}
          </div>
          <div style={{ marginTop: '8px' }}>
            <FileViewer
              shipmentId={shipment.shipment_id}
              shipment={shipment}
              shipmentHost={getShipmentActionHost(shipment)}
              showGenerateBolButton={false}
              deadDropPhotos={deadDropPhotos}
              shipFriendId={shipment.shipment_friendly_id}
            />
            <div style={{ marginTop: '6px' }}>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Add document</strong>
              <FileUpload shipmentId={shipment.shipment_id} shipmentFriendlyId={shipment.shipment_friendly_id} />
            </div>
          </div>
          <div style={{ lineHeight: 1.4, marginTop: '8px' }}>
            <div>
              <strong>Ordered By:</strong> {orderPlacedBy}
              {isCustomerDispatchChatAvailable(shipment) ? (
                <button
                  type="button"
                  onClick={() => openShipmentChat('customer_dispatch')}
                  aria-label="Open dispatch chat"
                  title="Open dispatch chat"
                  style={{ background: 'transparent', border: 'none', padding: 0, marginLeft: '8px' }}
                >
                  <span aria-label="Chat bubble" style={CHAT_BUBBLE_STYLE}>...</span>
                </button>
              ) : null}
            </div>
            {renderPhoneWithBubble(customer.phone)}
          </div>
        </Col>
      </Row>
    );
  };

  const renderLoadDetails = () => {
    const feeBlob = getShipmentFeeBlob();
    const calculatedBlob = feeBlob.calculated && typeof feeBlob.calculated === 'object' ? feeBlob.calculated : {};
    const shipmentBlob = feeBlob.shipment && typeof feeBlob.shipment === 'object' ? feeBlob.shipment : {};
    const shippingFeeRange = calculatedBlob.shipping_fee_range && typeof calculatedBlob.shipping_fee_range === 'object' ? calculatedBlob.shipping_fee_range : null;

    return (
      <>
        {shipmentBlob.is_ltl ? <p><strong>LTL Freight</strong></p> : null}
        {shipment.merchant_order_id ? <p>Store Order ID: <strong>{shipment.merchant_order_id}</strong></p> : null}
        <p>{getVehicleTypeLabel(shipment.vehicle_type)}</p>
        <p>{shipment.weight || 0} lbs</p>
        <p>{shipment.num_of_gophrs || 0} Gophrs Needed</p>
        {shipment.distance ? <p>{Number(shipment.distance).toFixed(2)} miles</p> : null}
        {getOrderPlacedByName() ? <p>Placed By: {getOrderPlacedByName()}</p> : null}
        {shipment.scheduled_for ? <p>Scheduled For: {formatTimestamp(shipment.scheduled_for)}</p> : null}
        {shipment.is_roundtrip ? <p>Round Trip</p> : null}
        {shipmentBlob.is_personal_protective_equipment ? <p>PPE required</p> : null}
        {Object.prototype.hasOwnProperty.call(calculatedBlob, 'hand_load_fee') ? <p>Hand Load: ${formatCurrency(calculatedBlob.hand_load_fee)}</p> : null}
        {Object.prototype.hasOwnProperty.call(calculatedBlob, 'dolly_fee') ? <p>Dolly: ${formatCurrency(calculatedBlob.dolly_fee)}</p> : null}
        {Object.prototype.hasOwnProperty.call(calculatedBlob, 'pallet_jack_fee') ? <p>Pallet Jack: ${formatCurrency(calculatedBlob.pallet_jack_fee)}</p> : null}
        {Object.prototype.hasOwnProperty.call(calculatedBlob, 'twic_card_fee') ? <p>TWIC Card: ${formatCurrency(calculatedBlob.twic_card_fee)}</p> : null}
        {Object.prototype.hasOwnProperty.call(calculatedBlob, 'liftgate_fee') ? <p>Liftgate: ${formatCurrency(calculatedBlob.liftgate_fee)}</p> : null}
        {Object.prototype.hasOwnProperty.call(calculatedBlob, 'hazmat_fee') ? <p>Hazmat: ${formatCurrency(calculatedBlob.hazmat_fee)}</p> : null}
        {Object.prototype.hasOwnProperty.call(calculatedBlob, 'weather_protection_fee') ? <p>Weather Protection: ${formatCurrency(calculatedBlob.weather_protection_fee)}</p> : null}
        {Object.prototype.hasOwnProperty.call(calculatedBlob, 'medical_fee') ? <p>Medical: ${formatCurrency(calculatedBlob.medical_fee)}</p> : null}
        <p>
          ${Number(shipment.shipping_fee) !== 0
            ? formatCurrency(shipment.shipping_fee)
            : shippingFeeRange
            ? `${formatCurrency(shippingFeeRange.min)}--${formatCurrency(shippingFeeRange.max)}`
            : formatCurrency(shipment.shipping_fee)}
        </p>
      </>
    );
  };

  const renderLocationInfo = (order, title) => {
    if (!order || !order.location_blob) {
      return null;
    }

    const location = order.location_blob;
    const completedValue = order.completed_at ? formatTimestamp(order.completed_at) : null;

    const chatRole = title.includes('Pickup') ? 'Pickup' : 'Dropoff';

    return (
      <div>
        <strong>{title}</strong>
        <p>{location.name || 'N/A'}</p>
        <p style={{ alignItems: 'center', display: 'flex', gap: '8px', marginBottom: 0 }}>
          <span>{location.phone || 'N/A'}</span>
          {isAdminViewer && location.phone ? (
            <button
              type="button"
              onClick={() => openChatHistory({
                chatDisplayName: location.name || title,
                chatRole,
                displayName: location.name || title,
                phone: location.phone,
              })}
              aria-label="Open chat history"
              title="Open chat history"
              style={{ background: 'transparent', border: 'none', padding: 0 }}
              disabled={isShipmentArchived}
            >
              <span aria-label="Chat bubble" style={CHAT_BUBBLE_STYLE}>...</span>
            </button>
          ) : null}
        </p>
        <p>{location.email || 'N/A'}</p>
        {location.business_name ? <p>Business Name: {location.business_name}</p> : null}
        <p>{location.address_1 || 'N/A'}</p>
        <p>{[location.city, location.state, location.zip].filter(Boolean).join(', ') || 'N/A'}</p>
        {completedValue ? <p>{completedValue}{COMPLETED_MARK}</p> : null}
        <PhotoGallery photos={order.photos} buttonText="View Photos" />
      </div>
    );
  };

  const renderRegularInstructions = () => (
    <>
      <Col className="col-2" style={{ textAlign: 'left' }}>
        <strong>Pickup Instructions</strong>
        <p>{shipment.pick_up_instructions || 'None'}</p>
      </Col>
      <Col className="col-2" style={{ marginLeft: '24px', textAlign: 'left' }}>
        <strong>Dropoff Instructions</strong>
        <p>{shipment.drop_off_instructions || 'None'}</p>
      </Col>
    </>
  );

  const renderDeliveryDescription = () => {
    const description = typeof shipment.description === 'string' ? shipment.description : '';

    if (description.includes('{') && description.includes('[') && description.includes(':') && description.includes('quantity') && description.includes('detail')) {
      try {
        const parsed = JSON.parse(description);
        return parsed.map((entry, index) => (
          <p key={`${entry.quantity}-${entry.detail}-${index}`}>{entry.quantity}-{entry.detail}</p>
        ));
      } catch (_) {
        return <p>{description || 'N/A'}</p>;
      }
    }

    return <p>{description || 'N/A'}</p>;
  };

  const getLocationCoordinates = locationBlob => {
    if (!locationBlob || typeof locationBlob !== 'object') {
      return null;
    }

    const parseCoordinate = value => {
      if (typeof value === 'number') {
        return value;
      }

      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : Number.NaN;
    };

    const latitude = parseCoordinate(locationBlob.lat !== undefined ? locationBlob.lat : locationBlob.latitude);
    const longitude = parseCoordinate(locationBlob.lng !== undefined ? locationBlob.lng : locationBlob.longitude);

    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return { lat: latitude, lng: longitude };
    }

    if (Array.isArray(locationBlob.coordinates) && locationBlob.coordinates.length >= 2) {
      const coordinatesLongitude = parseCoordinate(locationBlob.coordinates[0]);
      const coordinatesLatitude = parseCoordinate(locationBlob.coordinates[1]);

      if (Number.isFinite(coordinatesLatitude) && Number.isFinite(coordinatesLongitude)) {
        return { lat: coordinatesLatitude, lng: coordinatesLongitude };
      }
    }

    return null;
  };

  const getWaypointCoordinates = () => {
    const waypointCoordinates = orders
      .map(order => (order && order.location_blob ? getLocationCoordinates(order.location_blob) : null))
      .filter(Boolean);

    return waypointCoordinates;
  };

  const getCompletedAtTimestamp = () => {
    const completedAtValue = shipment && shipment.completed_at ? shipment.completed_at : null;
    const completedAtMs = completedAtValue ? new Date(completedAtValue).getTime() : Number.NaN;

    return Number.isFinite(completedAtMs) ? completedAtMs : Number.NaN;
  };

  const getTrackingCoordinates = () => {
    const parseCoordinate = value => {
      if (typeof value === 'number') {
        return value;
      }

      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : Number.NaN;
    };

    const normalizePoint = point => {
      if (!point || typeof point !== 'object') {
        return null;
      }

      const latitude = parseCoordinate(point.latitude !== undefined ? point.latitude : point.lat);
      const longitude = parseCoordinate(point.longitude !== undefined ? point.longitude : point.lng);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return null;
      }

      const rawTimestamp = point.timestamp || point.time || point.updated_at || point.created_at || null;
      const timestampMs = rawTimestamp ? new Date(rawTimestamp).getTime() : Number.NaN;

      return {
        lat: latitude,
        lng: longitude,
        timestampMs: Number.isFinite(timestampMs) ? timestampMs : Number.NaN,
      };
    };

    const completedAtMs = getCompletedAtTimestamp();
    const routeDrivers = route && Array.isArray(route.RouteDrivers) ? route.RouteDrivers : [];
    const primaryRouteDriver = routeDrivers.find(driver => driver && driver.tracking_blob && Array.isArray(driver.tracking_blob.trackingDetails)) || routeDrivers[0] || null;
    const tqlTrackingDetails = primaryRouteDriver && primaryRouteDriver.tracking_blob && Array.isArray(primaryRouteDriver.tracking_blob.trackingDetails)
      ? primaryRouteDriver.tracking_blob.trackingDetails
      : [];
    const locationUpdates = Array.isArray(shipment.LocationUpdates) ? shipment.LocationUpdates : [];

    const allTrackingPoints = tqlTrackingDetails
      .concat(locationUpdates)
      .map(normalizePoint)
      .filter(Boolean)
      .filter(point => !Number.isFinite(completedAtMs) || !Number.isFinite(point.timestampMs) || point.timestampMs <= completedAtMs)
      .sort((left, right) => {
        if (!Number.isFinite(left.timestampMs) && !Number.isFinite(right.timestampMs)) {
          return 0;
        }

        if (!Number.isFinite(left.timestampMs)) {
          return -1;
        }

        if (!Number.isFinite(right.timestampMs)) {
          return 1;
        }

        return left.timestampMs - right.timestampMs;
      });

    const dedupedPoints = [];
    const seenPointKeys = new Set();

    allTrackingPoints.forEach(point => {
      const pointKey = `${point.lat.toFixed(5)}:${point.lng.toFixed(5)}:${Number.isFinite(point.timestampMs) ? point.timestampMs : 'na'}`;

      if (seenPointKeys.has(pointKey)) {
        return;
      }

      seenPointKeys.add(pointKey);
      dedupedPoints.push(point);
    });

    return dedupedPoints;
  };

  const samplePointsForStaticMaps = (points, maxPoints) => {
    if (!Array.isArray(points) || points.length === 0 || !Number.isFinite(maxPoints) || maxPoints <= 0) {
      return [];
    }

    if (points.length <= maxPoints) {
      return points;
    }

    if (maxPoints === 1) {
      return [points[0]];
    }

    const sampledPoints = [];
    const interval = (points.length - 1) / (maxPoints - 1);

    for (let index = 0; index < maxPoints; index += 1) {
      const pointIndex = Math.round(index * interval);
      sampledPoints.push(points[pointIndex]);
    }

    return sampledPoints;
  };

  const getRouteLineCoordinates = waypointCoordinates => {
    const trackingCoordinates = getTrackingCoordinates();

    if (trackingCoordinates.length >= 2) {
      return samplePointsForStaticMaps(trackingCoordinates, 80);
    }

    return samplePointsForStaticMaps(Array.isArray(waypointCoordinates) ? waypointCoordinates : [], 80);
  };

  const getBreadcrumbCoordinates = routeLineCoordinates => {
    const normalizedRouteLine = Array.isArray(routeLineCoordinates) ? routeLineCoordinates : [];

    if (normalizedRouteLine.length <= 2) {
      return [];
    }

    return samplePointsForStaticMaps(normalizedRouteLine.slice(1, -1), 24);
  };

  const getGoogleStaticMapSnapshotUrl = (waypointCoordinates, routeLineCoordinates, breadcrumbCoordinates) => {
    if (!Array.isArray(waypointCoordinates) || waypointCoordinates.length === 0) {
      return '';
    }

    const mapAccessKey = dashboardConfig && dashboardConfig.config && dashboardConfig.config.GOOGLE_MAP
      ? dashboardConfig.config.GOOGLE_MAP.accessKey
      : '';

    if (!mapAccessKey) {
      return '';
    }

    const queryParams = new URLSearchParams();
    queryParams.set('size', '480x260');
    queryParams.set('scale', '2');
    queryParams.set('maptype', 'roadmap');

    if (Array.isArray(routeLineCoordinates) && routeLineCoordinates.length > 1) {
      const pathPoints = routeLineCoordinates
        .map(point => `${point.lat},${point.lng}`)
        .join('|');
      queryParams.append('path', `color:0xe53935|weight:4|${pathPoints}`);
    }

    if (Array.isArray(breadcrumbCoordinates) && breadcrumbCoordinates.length > 0) {
      const breadcrumbPoints = breadcrumbCoordinates
        .map(point => `${point.lat},${point.lng}`)
        .join('|');
      queryParams.append('markers', `size:tiny|color:0x90a4ae|${breadcrumbPoints}`);
    }

    const pickupPoint = waypointCoordinates[0];
    const dropoffPoint = waypointCoordinates[waypointCoordinates.length - 1];

    if (pickupPoint) {
      queryParams.append('markers', `color:0x2e7d32|label:P|${pickupPoint.lat},${pickupPoint.lng}`);
    }

    if (dropoffPoint) {
      queryParams.append('markers', `color:0xc62828|label:D|${dropoffPoint.lat},${dropoffPoint.lng}`);
    }

    queryParams.set('key', mapAccessKey);

    return `https://maps.googleapis.com/maps/api/staticmap?${queryParams.toString()}`;
  };

  const encodePolyline = coordinates => {
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      return '';
    }

    let previousLat = 0;
    let previousLng = 0;

    const encodeSignedValue = value => {
      let shiftedValue = value < 0 ? ~(value << 1) : value << 1;
      let encoded = '';

      while (shiftedValue >= 0x20) {
        encoded += String.fromCharCode((0x20 | (shiftedValue & 0x1f)) + 63);
        shiftedValue >>= 5;
      }

      encoded += String.fromCharCode(shiftedValue + 63);
      return encoded;
    };

    return coordinates.reduce((encodedPath, point) => {
      const currentLat = Math.round(point.lat * 1e5);
      const currentLng = Math.round(point.lng * 1e5);
      const deltaLat = currentLat - previousLat;
      const deltaLng = currentLng - previousLng;

      previousLat = currentLat;
      previousLng = currentLng;

      return `${encodedPath}${encodeSignedValue(deltaLat)}${encodeSignedValue(deltaLng)}`;
    }, '');
  };

  const getMapboxStaticMapSnapshotUrl = (waypointCoordinates, routeLineCoordinates, breadcrumbCoordinates) => {
    if (!Array.isArray(waypointCoordinates) || waypointCoordinates.length === 0 || !MAPBOX_STATIC_TOKEN) {
      return '';
    }

    const pickupPoint = waypointCoordinates[0];
    const dropoffPoint = waypointCoordinates[waypointCoordinates.length - 1];

    if (!pickupPoint || !dropoffPoint) {
      return '';
    }

    const overlays = [
      `pin-s-p+2e7d32(${pickupPoint.lng},${pickupPoint.lat})`,
      `pin-s-d+c62828(${dropoffPoint.lng},${dropoffPoint.lat})`
    ];

    if (Array.isArray(routeLineCoordinates) && routeLineCoordinates.length > 1) {
      const encodedPolyline = encodePolyline(routeLineCoordinates);

      if (encodedPolyline) {
        overlays.unshift(`path-4+e53935-0.9(${encodeURIComponent(encodedPolyline)})`);
      }
    }

    const waypointPins = waypointCoordinates
      .slice(1, -1)
      .map(point => `pin-s-circle+455a64(${point.lng},${point.lat})`);

    const breadcrumbPins = Array.isArray(breadcrumbCoordinates)
      ? breadcrumbCoordinates.map(point => `pin-s+90a4ae(${point.lng},${point.lat})`)
      : [];

    const overlayString = overlays.concat(waypointPins).concat(breadcrumbPins).join(',');

    return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${overlayString}/auto/480x260?padding=24,24,24,24&access_token=${encodeURIComponent(MAPBOX_STATIC_TOKEN)}`;
  };

  const renderCompletedMapSnapshot = () => {
    const waypointCoordinates = getWaypointCoordinates();
    const routeLineCoordinates = getRouteLineCoordinates(waypointCoordinates);
    const breadcrumbCoordinates = getBreadcrumbCoordinates(routeLineCoordinates);
    const googleSnapshotUrl = getGoogleStaticMapSnapshotUrl(waypointCoordinates, routeLineCoordinates, breadcrumbCoordinates);
    const mapboxSnapshotUrl = getMapboxStaticMapSnapshotUrl(waypointCoordinates, routeLineCoordinates, breadcrumbCoordinates);
    const mapSnapshotUrl = mapSnapshotProvider === 'google' ? googleSnapshotUrl : mapboxSnapshotUrl;
    const mapSnapshotLargeUrl = mapSnapshotUrl || googleSnapshotUrl || mapboxSnapshotUrl;

    if (waypointCoordinates.length === 0) {
      return null;
    }

    const pickupPoint = waypointCoordinates[0];
    const dropoffPoint = waypointCoordinates[waypointCoordinates.length - 1];
    const fallbackDirectionsUrl = pickupPoint && dropoffPoint
      ? `https://www.google.com/maps/dir/?api=1&origin=${pickupPoint.lat},${pickupPoint.lng}&destination=${dropoffPoint.lat},${dropoffPoint.lng}`
      : '';

    return (
      <>
        <button
          type="button"
          onClick={() => setIsMapSnapshotOpen(true)}
          style={{
            width: '360px',
            minWidth: '360px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '8px',
            backgroundColor: 'rgba(17, 19, 34, 0.45)',
            padding: '10px',
            textAlign: 'left',
            cursor: 'pointer'
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: '8px', color: '#d7d9e0' }}>Completed Route Snapshot</div>
          {mapSnapshotUrl ? (
            <img
              src={mapSnapshotUrl}
              alt={`Completed route snapshot for shipment ${shipment.shipment_friendly_id || shipment.shipment_id || ''}`}
              onError={() => {
                if (mapSnapshotProvider === 'google' && mapboxSnapshotUrl) {
                  setMapSnapshotProvider('mapbox');
                  return;
                }

                setMapSnapshotProvider('none');
              }}
              style={{
                width: '100%',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'block'
              }}
            />
          ) : (
            <div style={{
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '6px',
              padding: '12px',
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '13px'
            }}>
              Route snapshot unavailable for this shipment.
              {fallbackDirectionsUrl ? (
                <div style={{ marginTop: '8px' }}>
                  <a href={fallbackDirectionsUrl} target="_blank" rel="noopener noreferrer" onClick={event => event.stopPropagation()}>
                    Open route in Google Maps
                  </a>
                </div>
              ) : null}
            </div>
          )}
          <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.72)' }}>
            Ship ID: {shipment.shipment_friendly_id || shipment.shipment_id || 'N/A'}
            {completedAtDisplayValue ? ` • ${formatTimestamp(completedAtDisplayValue)}` : ''}
          </div>
          <div style={{ marginTop: '6px', fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)' }}>
            Click to open full view
          </div>
        </button>
        <Modal isOpen={isMapSnapshotOpen} toggle={() => setIsMapSnapshotOpen(false)} size="xl" className="iphone-chat-modal">
          <ModalBody style={{ backgroundColor: '#17192d', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div>
                <strong style={{ color: '#fff' }}>Completed Route Snapshot</strong>
                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>
                  Ship ID: {shipment.shipment_friendly_id || shipment.shipment_id || 'N/A'}
                </div>
              </div>
              {fallbackDirectionsUrl ? (
                <a href={fallbackDirectionsUrl} target="_blank" rel="noopener noreferrer">
                  Open in Google Maps
                </a>
              ) : null}
            </div>
            {mapSnapshotLargeUrl ? (
              <img
                src={mapSnapshotLargeUrl}
                alt={`Completed route snapshot for shipment ${shipment.shipment_friendly_id || shipment.shipment_id || ''}`}
                onError={() => {
                  if (mapSnapshotProvider === 'google' && mapboxSnapshotUrl) {
                    setMapSnapshotProvider('mapbox');
                    return;
                  }

                  setMapSnapshotProvider('none');
                }}
                style={{
                  width: '100%',
                  maxHeight: '75vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  display: 'block',
                  backgroundColor: '#0f1220'
                }}
              />
            ) : (
              <div style={{
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '6px',
                padding: '16px',
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px'
              }}>
                Route snapshot unavailable for this shipment.
              </div>
            )}
          </ModalBody>
          <ModalFooter style={{ backgroundColor: '#17192d', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <Button color="default" onClick={() => setIsMapSnapshotOpen(false)}>Close</Button>
          </ModalFooter>
        </Modal>
      </>
    );
  };

  const renderDriverInfo = () => {
    const currentDriver = getCurrentRouteDriver();

    if (!currentDriver) {
      return (
        <Col className="col-2" style={{ textAlign: 'left' }}>
          <strong>Driver Information</strong>
          <p>Awaiting driver</p>
        </Col>
      );
    }

    const driverBlob = currentDriver.driver_blob || {};
    const vehicleBlob = currentDriver.vehicle_blob || {};
    const driverPayAmount = getDriverPayAmount(currentDriver);

    return (
      <Col className="col-2" style={{ textAlign: 'left' }}>
        <strong>Driver Information</strong>
        <p>
          {[driverBlob.first_name, driverBlob.last_name].filter(Boolean).join(' ') || 'Assigned driver'}
          {isCustomerDriverChatAvailable(shipment) ? (
            <button
              type="button"
              onClick={() => openShipmentChat('customer_driver')}
              aria-label="Open driver chat"
              title="Open driver chat"
              style={{ background: 'transparent', border: 'none', padding: 0, marginLeft: '8px' }}
            >
              <span aria-label="Chat bubble" style={CHAT_BUBBLE_STYLE}>...</span>
            </button>
          ) : null}
        </p>
        {driverPayAmount !== null ? <p>Driver Pay: ${formatCurrency(driverPayAmount)}</p> : null}
        {isAdminViewer ? (
          (() => {
            const unpaidPays = driverPayResolution.payRecords.filter(
              pay => pay && !pay.payed_status && !driverPayResolution.resolvedPayIds.includes(pay.pay_id)
            );
            return (
              <div style={{ marginTop: '8px' }}>
                {!driverPayResolution.open ? (
                  <Button
                    size="sm"
                    color="warning"
                    style={{ padding: '4px 10px', margin: 0, whiteSpace: 'normal' }}
                    onClick={loadUnpaidDriverPays}
                  >
                    Mark driver paid (outside Branch)
                  </Button>
                ) : (
                  <div style={{ border: '1px solid #e3e3e3', borderRadius: 6, padding: 10, background: '#f7f7f8', color: '#212529' }}>
                    <strong style={{ color: '#7A5900' }}>Resolve payment outside Branch</strong>
                    {driverPayResolution.loading ? <p style={{ margin: '6px 0' }}>Loading…</p> : null}
                    {!driverPayResolution.loading && unpaidPays.length === 0 ? (
                      <p style={{ margin: '6px 0' }}>No unpaid driver pay for this order.</p>
                    ) : null}
                    {unpaidPays.length > 0 ? (
                      <>
                        <input
                          type="text"
                          value={driverPayResolution.reference}
                          onChange={e => setDriverPayResolution(prev => ({ ...prev, reference: e.target.value }))}
                          maxLength={200}
                          placeholder="ACH, check, or transaction reference"
                          style={{ display: 'block', width: '100%', margin: '6px 0', padding: '4px 6px', color: '#212529' }}
                        />
                        <input
                          type="text"
                          value={driverPayResolution.note}
                          onChange={e => setDriverPayResolution(prev => ({ ...prev, note: e.target.value }))}
                          maxLength={1000}
                          placeholder="How and when the driver was paid"
                          style={{ display: 'block', width: '100%', margin: '6px 0', padding: '4px 6px', color: '#212529' }}
                        />
                        {unpaidPays.map(pay => (
                          <Button
                            key={pay.pay_id}
                            size="sm"
                            color="success"
                            block
                            disabled={Boolean(driverPayResolution.resolvingPayId) || !driverPayResolution.reference.trim() || !driverPayResolution.note.trim()}
                            style={{ margin: '4px 0' }}
                            onClick={() => resolveDriverPayExternally(pay)}
                          >
                            {driverPayResolution.resolvingPayId === pay.pay_id
                              ? 'Resolving…'
                              : `Mark $${formatCurrency(Number(pay.total_earning || 0))} resolved`}
                          </Button>
                        ))}
                      </>
                    ) : null}
                    {driverPayResolution.error ? <p style={{ color: '#D62828', margin: '6px 0 0' }}>{driverPayResolution.error}</p> : null}
                    {driverPayResolution.message ? <p style={{ color: '#1a7f37', margin: '6px 0 0' }}>{driverPayResolution.message}</p> : null}
                  </div>
                )}
              </div>
            );
          })()
        ) : null}
        <p style={{ alignItems: 'center', display: 'flex', gap: '8px', marginBottom: 0 }}>
          <span>{driverBlob.phone || 'N/A'}</span>
          {isAdminViewer && driverBlob.phone ? (
            <button
              type="button"
              onClick={() => openChatHistory({
                chatDisplayName: [driverBlob.first_name, driverBlob.last_name].filter(Boolean).join(' ') || 'Assigned driver',
                chatRole: 'Driver',
                displayName: [driverBlob.first_name, driverBlob.last_name].filter(Boolean).join(' ') || 'Assigned driver',
                phone: driverBlob.phone,
              })}
              aria-label="Open chat history"
              title="Open chat history"
              style={{ background: 'transparent', border: 'none', padding: 0 }}
              disabled={isShipmentArchived}
            >
              <span aria-label="Chat bubble" style={CHAT_BUBBLE_STYLE}>...</span>
            </button>
          ) : null}
        </p>
        {formatEmploymentTypeLabel(currentDriver.employment_type) ? <p>{formatEmploymentTypeLabel(currentDriver.employment_type)}</p> : null}
        {(vehicleBlob.year || vehicleBlob.make || vehicleBlob.model)
          ? <p>{[vehicleBlob.year, vehicleBlob.make, vehicleBlob.model].filter(Boolean).join(' ')}{vehicleBlob.color ? ` (${vehicleBlob.color})` : ''}</p>
          : null}
        {currentDriver.tracking_blob && currentDriver.tracking_blob.po_number ? <p>ELD #: {currentDriver.tracking_blob.po_number}</p> : null}
      </Col>
    );
  };

  const renderStatusAndSignature = className => (
    <Col className={className} style={{ textAlign: 'left' }}>
      <strong>Status</strong>
      <p>{shipment.status === 'shipment_completed' ? <>Completed{COMPLETED_MARK}</> : formatStatus(shipment.status)}</p>
      {signatureHandshake ? (
        <a href={signatureHandshake.url} target="_blank" rel="noopener noreferrer">
          <img src={signatureHandshake.url} alt="Signature" height={100} />
        </a>
      ) : null}
    </Col>
  );

  const renderMultidropStops = () => {
    return orders.map((order, index) => {
      const title = index === 0 ? 'Pickup Information' : index === orders.length - 1 ? 'Dropoff Information' : 'Waypoint Information';
      const location = order && order.location_blob ? order.location_blob : null;

      if (!location) {
        return null;
      }

      return (
        <Col key={order.order_id || index} className="col-2.5" style={{ textAlign: 'left' }}>
          <strong>{title}</strong>
          <p>{location.name || 'N/A'}</p>
          {renderPhoneWithBubble(location.phone)}
          <p>{location.email || 'N/A'}</p>
          <p>{location.address_1 || 'N/A'}</p>
          <p>{[location.city, location.state, location.zip].filter(Boolean).join(', ') || 'N/A'}</p>
          {order.completed_at ? <p>{formatTimestamp(order.completed_at)}{COMPLETED_MARK}</p> : null}
          <PhotoGallery photos={order.photos} buttonText="View Photos" />
        </Col>
      );
    });
  };

  const renderDirectLayout = () => {
    const pickupOrder = getPickupOrder();
    const dropoffOrder = getDropoffOrder();

    return (
      <>
        <Row style={{ paddingLeft: '20px', paddingRight: '20px', paddingTop: '20px', paddingBottom: '10px' }}>
          <Col className="col-2" style={{ textAlign: 'left' }}>
            <strong>Load Details</strong>
            {renderLoadDetails()}
          </Col>
          <Col className="col-2" style={{ textAlign: 'left' }}>
            {renderLocationInfo(pickupOrder, 'Pickup Information')}
          </Col>
          <Col className="col-2" style={{ marginLeft: '24px', textAlign: 'left' }}>
            {renderLocationInfo(dropoffOrder, 'Dropoff Information')}
          </Col>
          {renderDriverInfo()}
        </Row>
        <Row style={{ paddingLeft: '20px', paddingRight: '20px', paddingTop: '10px', paddingBottom: '20px' }}>
          <Col className="col-2" style={{ textAlign: 'left' }}>
            <strong>Order Description</strong>
            {renderDeliveryDescription()}
          </Col>
          {renderRegularInstructions()}
          {shipment.ai_recommendation && shipment.ai_recommendation.recommendation ? (
            <Col className="col-3" style={{ textAlign: 'left' }}>
              <strong>AI Recommendation</strong>
              <p>{shipment.ai_recommendation.recommendation}</p>
            </Col>
          ) : null}
        </Row>
      </>
    );
  };

  const renderRoutedLayout = () => {
    return (
      <Row style={{ paddingLeft: '20px', paddingRight: '20px', paddingTop: '20px', paddingBottom: '10px', textAlign: 'left' }}>
        <Col className="col-2" style={{ textAlign: 'left' }}>
          <strong>Load Details</strong>
          {renderLoadDetails()}
          <p style={{ paddingBottom: '15px' }}>{shipment.status === 'shipment_completed' ? <>Completed{COMPLETED_MARK}</> : formatStatus(shipment.status)}</p>
        </Col>
        {renderDriverInfo()}
        {renderMultidropStops()}
      </Row>
    );
  };

  const renderMultidropLayout = () => {
    return (
      <Row style={{ paddingLeft: '20px', paddingRight: '20px', paddingTop: '10px', paddingBottom: '10px', textAlign: 'left' }}>
        <Col className="col-4" style={{ textAlign: 'left' }}>
          <strong>Load Details</strong>
          {renderLoadDetails()}
          <p style={{ paddingBottom: '15px' }}>{shipment.status === 'shipment_completed' ? <>Completed{COMPLETED_MARK}</> : formatStatus(shipment.status)}</p>
        </Col>
        {renderDriverInfo()}
        {renderMultidropStops()}
      </Row>
    );
  };

  const renderFallbackLayout = () => (
    <Row style={{ paddingLeft: '20px', paddingRight: '20px', paddingTop: '20px', paddingBottom: '10px' }}>
      <Col className="col-3" style={{ textAlign: 'left' }}>
        <strong>Load Details</strong>
        {renderLoadDetails()}
      </Col>
      <Col className="col-4" style={{ textAlign: 'left' }}>
        <strong>Dropoff Information</strong>
        <p>{getOrderPlacedByName()}</p>
        {renderPhoneWithBubble(customer.phone)}
        <p>{customer.address_1 || customer.address || 'N/A'}</p>
      </Col>
      {renderStatusAndSignature('col-3')}
    </Row>
  );

  const renderCustomerDriverReview = () => {
    if (!customerDriverReview || !customerDriverReview.rating) {
      return null;
    }

    const submittedAtLabel = customerDriverReview.submittedAt
      ? formatTimestamp(customerDriverReview.submittedAt)
      : 'Saved locally';
    const reviewText = customerDriverReview.reviewText || '';
    const filledStars = '★'.repeat(Math.max(0, Math.min(5, Number(customerDriverReview.rating || 0))));
    const emptyStars = '☆'.repeat(Math.max(0, 5 - Math.max(0, Math.min(5, Number(customerDriverReview.rating || 0)))));

    return (
      <div
        style={{
          marginTop: '16px',
          padding: '14px 16px',
          borderRadius: '10px',
          border: '1px solid rgba(255,255,255,0.14)',
          backgroundColor: 'rgba(255,255,255,0.06)',
          textAlign: 'left',
        }}
      >
        <strong style={{ display: 'block', marginBottom: '8px' }}>Customer Driver Review</strong>
        <p style={{ marginBottom: '8px', fontSize: '18px', letterSpacing: '0.08em', color: '#f4b400' }}>
          {filledStars}{emptyStars}
        </p>
        <p style={{ marginBottom: reviewText ? '8px' : 0 }}>
          <strong>Submitted:</strong> {submittedAtLabel}
        </p>
        {reviewText ? (
          <p style={{ marginBottom: 0 }}>
            <strong>Review:</strong> {reviewText}
          </p>
        ) : null}
      </div>
    );
  };

  const renderExpandedLayout = () => {
    if (shipment.shipment_type === 'direct' || shipment.shipment_type === 'connect') {
      return renderDirectLayout();
    }

    if (shipment.shipment_type === 'routed_direct') {
      return renderRoutedLayout();
    }

    if (shipment.shipment_type === 'multidrop_direct') {
      return renderMultidropLayout();
    }

    return renderFallbackLayout();
  };

  return (
    <>
      <tr onClick={toggle}>
        <td>{getOrderPlacedByName()}</td>
        <td>{shipment.shipment_friendly_id || 'N/A'}</td>
        <td>{completedAtDisplayValue ? formatTimestamp(completedAtDisplayValue) : 'N/A'}</td>
        <td>{formatStatus(shipment.status)}</td>
        <td>${formatCurrency(shipment.shipping_fee)}</td>
        <td>{formatInvoiceStatusLabel(invoicePaymentStatus)}</td>
      </tr>
      <tr>
        <td colSpan={expandedRowColumns} style={{ borderTop: 'none', padding: 0 }}>
          <Collapse isOpen={isOpen}>
            <div
              style={{
                backgroundColor: 'rgba(23, 25, 45, 0.55)',
                borderRadius: '6px',
                height: 'auto',
                marginBottom: '20px',
                marginLeft: '10px',
                marginRight: '10px',
                paddingBottom: '15px',
                paddingLeft: '10px',
                paddingRight: '10px',
                paddingTop: '15px'
              }}
            >
              {renderOrderHeader()}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {renderExpandedLayout()}
                  {renderCustomerDriverReview()}
                </div>
                {renderCompletedMapSnapshot()}
              </div>
              {renderCustomerDriverReview()}
            </div>
          </Collapse>
        </td>
      </tr>
      <Modal isOpen={showChatModal} toggle={toggleChatModal} className="iphone-chat-modal">
        <ModalBody className="iphone-chat-modal-body">
          <div className="iphone-chat-header">
            <div className="iphone-chat-header__title-row">
              <div className="iphone-chat-header__avatars">
                <div className="iphone-chat-avatar iphone-chat-avatar--self">You</div>
                <div className="iphone-chat-avatar iphone-chat-avatar--recipient">{getChatInitials(chatContact)}</div>
              </div>
              <div className="iphone-chat-header__meta">
                <h4 className="iphone-chat-header__title">{getChatTitle(chatContact)}</h4>
                <p className="iphone-chat-header__subtitle">
                  {(chatContact && (chatContact.chatDisplayName || chatContact.displayName)) || 'Contact'}
                  {chatContact && chatContact.chatShipmentFriendlyId ? ` • Shipment #${chatContact.chatShipmentFriendlyId}` : ''}
                </p>
              </div>
            </div>
            {chatContact && chatContact.chatArchived ? (
              <div style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.78)', fontSize: '13px' }}>
                Archive only. New messages are disabled for completed orders.
              </div>
            ) : null}
            <Button color="warning" size="sm" onClick={() => openChatHistory(chatContact)} disabled={chatLoading || !chatContact.phone || Boolean(chatContact && chatContact.chatArchived)}>
              Refresh
            </Button>
          </div>

          {chatLoading ? (
            <div style={{ padding: '24px', textAlign: 'center' }}>Loading messages...</div>
          ) : chatMessages.length > 0 ? (
            <MessageLogsContainer driverMessageRetrieved={chatMessages} messageRecipientInfo={chatContact} />
          ) : (
            <div style={{ padding: '24px', textAlign: 'center' }}>No messages found for this contact yet.</div>
          )}
        </ModalBody>
        <ModalFooter className="iphone-chat-modal-footer">
          <Button color="default" onClick={toggleChatModal}>Close</Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={shipmentChatOpen} toggle={resetShipmentChatState}>
        <ModalBody>
          <div style={{ marginBottom: '12px' }}>
            <strong>{shipmentChatInfo && shipmentChatInfo.chatTitle ? shipmentChatInfo.chatTitle : 'Shipment Chat'}</strong>
          </div>
          {shipmentChatInfo && shipmentChatInfo.chatArchived ? (
            <div style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.78)', fontSize: '13px' }}>
              Archive only. New messages are disabled for completed orders.
            </div>
          ) : null}
          {shipmentChatLoading ? (
            <div>Loading chat...</div>
          ) : (
            <MessageLogsContainer
              driverMessageRetrieved={shipmentChatMessages}
              messageRecipientInfo={shipmentChatInfo || {}}
            />
          )}
          {shipmentChatInfo && shipmentChatInfo.chatArchived ? null : (
            <textarea
              className="form-control merchant-shipment-chat-textarea"
              rows="4"
              value={shipmentChatMessage}
              onChange={event => setShipmentChatMessage(event.target.value)}
              placeholder="Type your message"
              style={{ marginTop: '12px', color: '#000000', backgroundColor: '#ffffff' }}
            />
          )}
        </ModalBody>
        <ModalFooter style={{ justifyContent: 'space-between' }}>
          <Button color="default" onClick={resetShipmentChatState}>Close</Button>
          {shipmentChatInfo && shipmentChatInfo.chatArchived ? null : (
            <Button color="info" onClick={sendShipmentChatMessage} disabled={shipmentChatSending}>
              {shipmentChatSending ? 'Sending...' : 'Send'}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Detailed;
